import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { fmtDT, gstatus } from "@/components/graphisme/gUtils";
import { eur } from "@/components/accounting/docUtils";

export default function GraphismeApercu() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/graphisme/apercu-client/${clientId}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [clientId]);

  if (data === null) return (
    <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
  );
  if (data === false) return <p className="py-20 text-center text-sm text-neutral-400">Client introuvable.</p>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="apercu-client-page">
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center gap-2 text-sm text-indigo-800" data-testid="apercu-banner">
        <Eye className="w-4 h-4 shrink-0" />
        <span><strong>Prévisualisation administrateur</strong> — ceci est exactement ce que voit le client. Aucune action n'est possible ici (lecture seule).</span>
        <Button size="sm" variant="outline" className="ml-auto h-7 text-xs shrink-0" data-testid="apercu-back"
          onClick={() => navigate(`/clients/${clientId}`)}>
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à la fiche
        </Button>
      </div>

      <h1 className="font-heading text-2xl font-bold" data-testid="apercu-title">Bonjour {data.client?.name} 👋</h1>

      {data.actions?.length > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-5 space-y-2" data-testid="apercu-actions">
          <p className="font-heading font-bold text-orange-900 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> Action requise</p>
          {data.actions.map((a) => (
            <p key={a.kind} className="text-sm text-orange-800 bg-white/60 border border-orange-200 rounded-lg px-3 py-2" data-testid={`apercu-action-${a.kind}`}>
              {a.text}
            </p>
          ))}
        </div>
      )}

      <section>
        <h2 className="font-heading font-bold text-lg mb-3">Projets en cours ({data.projects?.length || 0})</h2>
        <div className="space-y-2" data-testid="apercu-projects">
          {(data.projects || []).map((p) => {
            const st = gstatus(p.status);
            return (
              <div key={p.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid={`apercu-projet-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-[11px] text-neutral-400 font-mono">{p.number}</p>
                </div>
                <span className={`text-[11px] rounded-full px-2.5 py-1 border shrink-0 ${st.cls}`}>{st.label}</span>
              </div>
            );
          })}
          {(data.projects || []).length === 0 && <p className="text-sm text-neutral-400">Aucun projet en cours.</p>}
        </div>
      </section>

      <section>
        <h2 className="font-heading font-bold text-lg mb-3">Devis & factures</h2>
        <div className="space-y-2" data-testid="apercu-docs">
          {(data.quotes || []).map((q) => (
            <div key={q.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center justify-between text-sm">
              <span>Devis {q.number}</span>
              <span className="font-medium">{eur(q.total_ttc)}</span>
            </div>
          ))}
          {(data.quotes || []).length === 0 && <p className="text-sm text-neutral-400">Aucun document.</p>}
        </div>
      </section>
    </div>
  );
}
